import type { ForecastingRow } from '@/api/forecasting';
import type {
  ChannelSlice,
  DashboardFilters,
  DashboardTotals,
  FilterableRow,
  KpiLens,
  MetricField,
  MonthlyStageRow
} from '../forecasting.types';
import {
  buildFySelectOptions,
  monthInRange,
  parseIsoDate
} from './fyCalendar';

export {
  formatFyLabel,
  formatCurrentQuarterLabel,
  getCurrentFyCode,
  getCurrentFyQuarterRange,
  getDefaultDateFilters,
  getDefaultThreeMonthFilters,
  buildFySelectOptions,
  getFyDateRange,
  getPriorFyCode,
  shiftIsoDateByYears,
  toIsoDate
} from './fyCalendar';

import {
  formatMonthAxisLabel,
  formatMonthLabel,
  monthKeyFromDate,
  parseRowMonthDate
} from './forecastingDates';

export { formatMonthLabel, formatMonthAxisLabel, monthKeyFromDate, normalizeYear, parseRowMonthDate } from './forecastingDates';

import {
  getBudgetGp,
  getBudgetRevenue,
  getDynamicForecastGp,
  getDynamicForecastRevenue
} from './dynamicForecastMetrics';
import { chartStageForMonthRow } from './monthlyStageRules';

export function uniqueFilterOptions(rows: ForecastingRow[], field: keyof ForecastingRow): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    const v = r[field];
    if (typeof v === 'string' && v.trim()) set.add(v.trim());
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

export function uniqueFyOptions(rows: ForecastingRow[]): { label: string; value: string }[] {
  return buildFySelectOptions(uniqueFilterOptions(rows, 'FY'));
}

export function getRevenue(row: ForecastingRow): number {
  return getDynamicForecastRevenue(row);
}

export function getGp(row: ForecastingRow): number {
  return getDynamicForecastGp(row);
}

function isBudgetLens(lens?: KpiLens): boolean {
  return lens === 'budgetRevenue' || lens === 'budgetGp';
}

export function getMetricValue(row: ForecastingRow, metric: MetricField, lens?: KpiLens): number {
  if (isBudgetLens(lens)) {
    return metric === 'revenue' ? getBudgetRevenue(row) : getBudgetGp(row);
  }
  return metric === 'revenue' ? getRevenue(row) : getGp(row);
}

export function normalizeStage(
  stage?: string,
  status?: string,
  simplified?: string
): keyof Pick<MonthlyStageRow, 'Forecast' | 'Booked' | 'Pipe' | 'Other'> {
  const match = (raw: string): keyof Pick<MonthlyStageRow, 'Forecast' | 'Booked' | 'Pipe' | 'Other'> => {
    const t = raw.trim().toLowerCase();
    if (!t) return 'Other';
    if (t === 'booked' || t === 'book') return 'Booked';
    if (t === 'pipe' || t.includes('pipe')) return 'Pipe';
    if (t === 'forecast' || t === 'fc' || t === 'fcst' || t.includes('forecast')) return 'Forecast';
    return 'Other';
  };

  const fromSimplified = simplified ? match(simplified) : 'Other';
  if (fromSimplified !== 'Other') return fromSimplified;

  const s = stage?.trim().toLowerCase() ?? '';
  const st = status?.trim().toLowerCase() ?? '';

  if (s === 'dynamic forecast') {
    const fromStatus = match(st);
    if (fromStatus !== 'Other') return fromStatus;
    return 'Other';
  }

  const fromStage = match(s);
  if (fromStage !== 'Other') return fromStage;
  return match(st);
}

function rowMatchesFilters(row: FilterableRow, filters: DashboardFilters): boolean {
  if (filters.dateFrom && filters.dateTo) {
    const md = parseRowMonthDate(row);
    if (!md) return false;
    const rangeStart = parseIsoDate(filters.dateFrom);
    const rangeEnd = parseIsoDate(filters.dateTo);
    if (!monthInRange(md, rangeStart, rangeEnd)) return false;
  }

  const checks: [string[] | undefined, string | undefined][] = [
    [filters.channel, row.Channel],
    [filters.sales, row.Sales],
    [filters.buyingPoint, row.Buying_Point],
    [filters.am, row.AM],
    [filters.advertiser, row.Advertiser],
    [filters.status, row.Status],
    [filters.source, row.Source],
    [filters.clientType, row.Customer_Type],
    [filters.newExisting, row.New_Existing],
    [filters.territory, row.Territory],
    [filters.platform, row.Platform]
  ];

  /* Trimmed, as the options are: a value the sheet carries with a stray space
     was offered as a filter, then matched nothing when picked. */
  return checks.every(
    ([selected, rowVal]) => !selected?.length || (rowVal != null && selected.includes(String(rowVal).trim()))
  );
}

export function filterRows(rows: ForecastingRow[], filters: DashboardFilters): ForecastingRow[] {
  return rows.filter((r) => rowMatchesFilters(r, filters));
}

/** Filter to rows whose Month__Date_ falls in the given YYYY-MM bucket. */
export function filterRowsByMonthKey(rows: ForecastingRow[], monthKey: string): ForecastingRow[] {
  return rows.filter((row) => {
    const md = parseRowMonthDate(row);
    if (!md) return false;
    return monthKeyFromDate(md) === monthKey;
  });
}

/** Prior-year month key for YoY when a month is selected, e.g. 2026-06 → 2025-06. */
export function priorYearMonthKey(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  return `${Number(year) - 1}-${month}`;
}

export function sumMetric(rows: ForecastingRow[], metric: MetricField, lens?: KpiLens): number {
  return rows.reduce((s, r) => s + getMetricValue(r, metric, lens), 0);
}

export function computeTotals(rows: ForecastingRow[]): DashboardTotals {
  const dynamicRevenue = sumMetric(rows, 'revenue');
  const dynamicGp = sumMetric(rows, 'gp');
  const budgetRevenue = sumMetric(rows, 'revenue', 'budgetRevenue');
  const budgetGp = sumMetric(rows, 'gp', 'budgetGp');

  const vsBudgetRevenuePct =
    budgetRevenue > 0 ? (dynamicRevenue / budgetRevenue) * 100 : null;
  const vsBudgetGpPct = budgetGp > 0 ? (dynamicGp / budgetGp) * 100 : null;

  return { dynamicRevenue, dynamicGp, budgetRevenue, budgetGp, vsBudgetRevenuePct, vsBudgetGpPct };
}

/** Months in order between dateFrom and dateTo (inclusive). */
export function monthSequenceFromRange(
  dateFrom: string,
  dateTo: string
): { key: string; label: string; axisLabel: string; date: Date }[] {
  const start = parseIsoDate(dateFrom);
  const end = parseIsoDate(dateTo);
  const months: { key: string; label: string; axisLabel: string; date: Date }[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);

  while (monthInRange(cursor, start, end)) {
    months.push({
      key: monthKeyFromDate(cursor),
      label: formatMonthLabel(cursor),
      axisLabel: formatMonthAxisLabel(cursor),
      date: new Date(cursor)
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

export function groupByMonthAndStage(
  rows: ForecastingRow[],
  metric: MetricField,
  dateFrom: string,
  dateTo: string,
  lens?: KpiLens,
  asOfDate: Date = new Date()
): MonthlyStageRow[] {
  const seq = monthSequenceFromRange(dateFrom, dateTo);
  const map = new Map<string, MonthlyStageRow>();

  for (const { key, label, axisLabel } of seq) {
    map.set(key, {
      monthKey: key,
      label,
      axisLabel,
      Forecast: 0,
      Booked: 0,
      Pipe: 0,
      Other: 0,
      total: 0
    });
  }

  for (const row of rows) {
    const md = parseRowMonthDate(row);
    if (!md) continue;
    const key = monthKeyFromDate(md);
    const bucket = map.get(key);
    if (!bucket) continue;

    const value = getMetricValue(row, metric, lens);
    if (value === 0) continue;

    // Budget rows (Status = Budgetv2) are not Booked/Pipe/Forecast — show full FY as one series.
    if (isBudgetLens(lens)) {
      bucket.Booked += value;
      bucket.total += value;
      continue;
    }

    const stage = chartStageForMonthRow(md, row.Status, row.Simplified_Stages, asOfDate);
    if (!stage) continue;

    bucket[stage] += value;
    bucket.total += value;
  }

  return seq.map(({ key }) => map.get(key)!);
}

export function groupByChannel(
  rows: ForecastingRow[],
  metric: MetricField,
  lens?: KpiLens
): ChannelSlice[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    const name = row.Channel?.trim() || 'Other';
    map.set(name, (map.get(name) ?? 0) + getMetricValue(row, metric, lens));
  }

  const total = [...map.values()].reduce((a, b) => a + b, 0);
  return [...map.entries()]
    .map(([name, value]) => ({
      name,
      value,
      percent: total > 0 ? (value / total) * 100 : 0
    }))
    .sort((a, b) => b.value - a.value);
}

export function formatCurrency(val: number): string {
  return `£${val.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/**
 * Short money for labels on bars: £1.49m, £950k, £800. Zero or missing gives
 * '' so an empty month carries no label.
 */
export function formatCurrencyCompact(val: unknown): string {
  const n = Number(val);
  if (!Number.isFinite(n) || n === 0) return '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `£${(n / 1_000_000).toFixed(2)}m`;
  if (abs >= 1_000) return `£${Math.round(n / 1_000)}k`;
  return `£${Math.round(n)}`;
}

/** Tooltip / detail values (matches Looker hover precision). */
export function formatCurrencyPrecise(val: number): string {
  return `£${val.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatPercent(val: number, decimals = 0): string {
  return `${val.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;
}

export function metricFromLens(lens: KpiLens): MetricField {
  if (lens === 'dynamicForecastGp' || lens === 'budgetGp' || lens === 'vsBudgetGp') return 'gp';
  return 'revenue';
}
